import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useParams, withRouter } from 'react-router-dom';

import { Button, Form, Typography } from 'antd';

import messages from '../../../constants/messages';

import { useSetState } from '../../../hooks/useSetState';

import { setPassword, verifyToken } from '../../../redux/actions/user';

import AgreementField from '../../../components/AgreementField';
import PasswordField from '../../../components/PasswordField';
import { errorToastHandler } from '../../../components/action_notifier';

import AuthLayout from '../../../layouts/AuthLayout';

const { Title } = Typography;

const SetPassword = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { token } = useParams();

  const [email, setEmail] = useState('');
  const [isFormSubmitted, setIsFormSubmitted] = useSetState(false);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [submittable, setSubmittable] = useState(false);
  const [tokenVerificationErrorMsg, setTokenVerificationErrorMsg] = useState('');

  const values = Form.useWatch([], form);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable?.(true))
      .catch(() => setSubmittable?.(false));
  }, [values]);

  useEffect(() => {
    try {
      dispatch(verifyToken({ token }))
        .then(response => {
          setIsTokenValid(true);
          setEmail(response?.email);
        })
        .catch(error => errorToastHandler(error?.response?.data?.message || messages.DEFAULT_ERROR));
    } catch (error) {
      setTokenVerificationErrorMsg(error?.message);
    }
  }, [dispatch]);

  const submitPassword = async e => {
    e.preventDefault();

    try {
      const { password, isTermsAccepted } = await form.validateFields();
      await dispatch(setPassword({ token, password, isTermsAccepted }));
      form.resetFields();
      setIsFormSubmitted(true);
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.DEFAULT_ERROR);
    }
  };

  return (
    <AuthLayout asideContent={{ text: 'We’ll get you back on the Kraal platform shortly.' }}>
      <Title className="mb-4" level={3}>
        Set Password {email ?? `for ${email}`}
      </Title>
      {isTokenValid && !isFormSubmitted && (
        <Form form={form} className="mt-3" layout="vertical" requiredMark={false} size="small">
          <PasswordField
            name="password"
            rules={[
              { required: true, message: messages.FIELD_REQUIRED('Password') },
              {
                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/,
                message: messages.PASSWORD_INVALID
              }
            ]}
          />

          <PasswordField
            dependencies={['password']}
            label="Confirm Password"
            name="password_confirm"
            rules={[
              {
                required: true,
                message: messages.FIELD_REQUIRED('Confirm Password')
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  return getFieldValue('password') === value
                    ? Promise.resolve()
                    : Promise.reject(new Error(messages.PASSWORDS_MISMATCH));
                }
              })
            ]}
          />

          <AgreementField name="isTermsAccepted" />

          <Button className="w-100 mt-4" size="large" type="primary" disabled={!submittable} onClick={submitPassword}>
            Submit Password
          </Button>
        </Form>
      )}
      {!isTokenValid && (
        <div className="pt-3">
          <p className="text-red">{tokenVerificationErrorMsg}</p>
          <Link to="/auth/recover-pwd">Get new token</Link>
        </div>
      )}
      {isFormSubmitted && (
        <div className="pt-3">
          <p>Your password has been set. You can now login with your new password.</p>
          <Link to="/auth/login">Go to Login</Link>
        </div>
      )}
    </AuthLayout>
  );
};

export default withRouter(SetPassword);
